import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import {
	ALL_IMAGE_SOURCES,
	IMAGE_SOURCE_FILTERS,
	toImageSourceFilter,
	useImageLibraryQuery,
	useRemoveFromImageLibraryMutation,
	useRenameImageLibraryEntryMutation,
} from '../../../queries/ImageLibrary';
import { Button } from '../Button';
import { ImageLibraryGrid } from '../ImageLibraryGrid';
import { Input } from '../Input';
import { TabToggle } from '../TabToggle';
import { UnifiedImageDialog } from '../UnifiedImageDialog';
import { styleSheet } from './styles';

const MAX_LIBRARY_UPLOAD_IMAGES = 25;

export interface ImageLibraryManagerProps {
	/** Set by the admin app so reads and writes hit the customer's library, not the admin's own account */
	impersonationContext?: Api.IImpersonationContext;
	heading: string;
	description: string;
	emptyTitle: string;
	emptyDescription: string;
}

/**
 * Browse, search, and clean up an account's image library. Shared by the customer-facing Campaigns tab and
 * the admin account page, which differ only in copy and whether calls carry an impersonation context.
 *
 * This is the only place images can leave the library. Everything else in the product adds to it, which is
 * what makes a cleanup surface necessary rather than nice to have.
 */
export function ImageLibraryManager({
	impersonationContext,
	heading,
	description,
	emptyTitle,
	emptyDescription,
}: ImageLibraryManagerProps) {
	const [searchTerm, setSearchTerm] = React.useState('');
	const [sourceFilter, setSourceFilter] = React.useState<string>(ALL_IMAGE_SOURCES);
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
	const [confirmingRemove, setConfirmingRemove] = React.useState(false);
	// The mutation's isPending flips once per image, so a batch would flicker the button.
	const [isRemovingBatch, setIsRemovingBatch] = React.useState(false);
	const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);

	const debouncedSearchTerm = useDebounceValue(searchTerm);

	const libraryQuery = useImageLibraryQuery({
		impersonationContext,
		query: debouncedSearchTerm,
		source: toImageSourceFilter(sourceFilter),
	});

	const removeMutation = useRemoveFromImageLibraryMutation();

	const renameMutation = useRenameImageLibraryEntryMutation({
		onError: () => toast.error('Could not rename this image. Try again.'),
	});

	const items = libraryQuery.data?.pages?.flatMap(page => page.values ?? []) ?? [];
	const isFiltered = !!debouncedSearchTerm || sourceFilter !== ALL_IMAGE_SOURCES;

	const toggleSelected = (item: Api.IImageLibraryItem) => {
		setSelectedIds(previous => {
			const next = new Set(previous);
			if (next.has(item.id)) {
				next.delete(item.id);
			} else {
				next.add(item.id);
			}
			return next;
		});
	};

	const removeSelected = async () => {
		const ids = [...selectedIds];
		setIsRemovingBatch(true);
		const outcomes = await Promise.allSettled(
			ids.map(itemId => removeMutation.mutateAsync({ impersonationContext, itemId }))
		);
		const failed = outcomes.filter(outcome => outcome.status === 'rejected').length;

		setIsRemovingBatch(false);
		setConfirmingRemove(false);

		if (failed) {
			// Keep the failures selected, otherwise "try the rest again" means hunting for them by hand.
			setSelectedIds(new Set(ids.filter((_, index) => outcomes[index].status === 'rejected')));
			toast.error(
				failed === ids.length
					? 'Could not remove those images. Try again.'
					: `Removed ${ids.length - failed} of ${ids.length}. Try the rest again.`
			);
			return;
		}

		setSelectedIds(new Set());

		toast.success(ids.length === 1 ? 'Removed from the library' : `Removed ${ids.length} images from the library`);
	};

	return (
		<div className={css(styleSheet.container)}>
			<header className={css(styleSheet.header)}>
				<div>
					<h2 className={css(styleSheet.heading)}>{heading}</h2>
					<p className={css(styleSheet.subheading)}>{description}</p>
				</div>
				<Button onClick={() => setUploadDialogOpen(true)} variant='primary'>
					Add images
				</Button>
			</header>

			<div className={css(styleSheet.controls)}>
				<Input
					autoComplete='off'
					id='image-library-manager-search'
					onChange={e => setSearchTerm(e.target.value)}
					placeholder='Search images'
					styles={[styleSheet.searchInput]}
					type='text'
					value={searchTerm}
				/>
				<TabToggle items={IMAGE_SOURCE_FILTERS} onValueChange={setSourceFilter} value={sourceFilter} />
			</div>

			{selectedIds.size > 0 && (
				<div className={css(styleSheet.selectionBar)}>
					<span className={css(styleSheet.selectionCount)}>
						{selectedIds.size === 1 ? '1 image selected' : `${selectedIds.size} images selected`}
					</span>
					{confirmingRemove ? (
						<>
							<Button onClick={() => setConfirmingRemove(false)} variant='secondary'>
								Keep them
							</Button>
							<Button isLoading={isRemovingBatch} onClick={removeSelected} variant='destructive'>
								Remove from library
							</Button>
						</>
					) : (
						<>
							<Button onClick={() => setSelectedIds(new Set())} variant='secondary'>
								Clear selection
							</Button>
							<Button onClick={() => setConfirmingRemove(true)} variant='destructive-secondary'>
								Remove from library
							</Button>
						</>
					)}
				</div>
			)}

			<ImageLibraryGrid
				emptyDescription={isFiltered ? 'Try a different search term or filter.' : emptyDescription}
				emptyTitle={isFiltered ? 'No matching images' : emptyTitle}
				hasNextPage={libraryQuery.hasNextPage}
				isFetchingNextPage={libraryQuery.isFetchingNextPage}
				isError={libraryQuery.isError}
				isLoading={libraryQuery.isLoading}
				items={items}
				onItemClick={toggleSelected}
				onLoadMore={() => libraryQuery.fetchNextPage()}
				onRetry={() => libraryQuery.refetch()}
				onRename={(item, displayName) => renameMutation.mutate({ displayName, impersonationContext, itemId: item.id })}
				selectedIds={selectedIds}
			/>

			<UnifiedImageDialog
				addUploadsToLibrary
				allowMultipleUpload
				impersonationContext={impersonationContext}
				initialTab='upload'
				// Bulk brand photos are the point of this button, so the count is generous; the client batches
				// the selection to stay under the request size limit rather than letting a big pick fail.
				maxUploadImages={MAX_LIBRARY_UPLOAD_IMAGES}
				onImageSelected={() => setUploadDialogOpen(false)}
				onMultipleImagesSelected={() => setUploadDialogOpen(false)}
				onOpenChange={setUploadDialogOpen}
				open={uploadDialogOpen}
				showFreeImagesTab={false}
			/>
		</div>
	);
}
